/**
 * Опции селектора языка распознавания.
 *
 * Названия берутся у браузера через Intl.DisplayNames, а не из наших словарей:
 * 93 языка × 4 локали — это 372 строки перевода, которые никто не будет
 * поддерживать. Английское название из справочника остаётся фолбэком для
 * кодов, которых Intl не знает (Northern Sotho, Asturian и т.п.).
 */
import { ASR_LANGUAGES, asrLanguageStoredCode, type AsrAccuracyTier } from '@aica/core';

const TIER_ORDER: readonly AsrAccuracyTier[] = ['excellent', 'high', 'good', 'moderate'];

export interface AsrLanguageOption {
  value: string;
  label: string;
  group?: string;
}

export function buildAsrLanguageOptions(
  uiLocale: string,
  t: (key: string) => string
): AsrLanguageOption[] {
  let display: Intl.DisplayNames | undefined;
  try {
    display = new Intl.DisplayNames([uiLocale], { type: 'language' });
  } catch {
    display = undefined;
  }

  const localized = (code: string, fallback: string): string => {
    if (!display) return fallback;
    try {
      const name = display.of(code);
      // Intl возвращает сам код, когда языка не знает — это не название.
      return !name || name === code ? fallback : name;
    } catch {
      return fallback;
    }
  };

  // Сортировка — по ТОМУ ЖЕ тексту, что человек читает, и по правилам его
  // локали. Раньше список сортировался по английскому `lang.name`, а показывал
  // подпись из Intl.DisplayNames: в ru/uk/es каждая группа точности шла в
  // порядке, не алфавитном ни по чему видимому («Німецька» между «Данська» и
  // «Грецька» — потому что German между Danish и Greek).
  const collator = (() => {
    try {
      return new Intl.Collator(uiLocale);
    } catch {
      return new Intl.Collator();
    }
  })();

  const options = ASR_LANGUAGES.map((lang) => ({
    tier: lang.tier,
    value: asrLanguageStoredCode(lang),
    label: localized(lang.iso1 ?? lang.iso3, lang.name),
    group: t(`settings:asrTier.${lang.tier}`),
  })).sort((a, b) => {
    const byTier = TIER_ORDER.indexOf(a.tier) - TIER_ORDER.indexOf(b.tier);
    return byTier !== 0 ? byTier : collator.compare(a.label, b.label);
  });

  return [
    { value: 'auto', label: t('settings:asrLanguageAuto') },
    ...options.map(({ value, label, group }) => ({ value, label, group })),
  ];
}
